import AverageResolutionTime from "./charts/AverageResolutionTime";
import ResolutionBeyondSLA from "./charts/ResolutionBeyondSLA";
import PerformanceSummaryCards from "./charts/PerformanceSummaryCards";
import TagWiseResolutionSLA from "./charts/TagWiseResolutionSLA";
import WorkflowStageResolutionSLA, {
  getWorkflowStageItems,
} from "./charts/WorkflowStageResolutionSLA";
import PriorityBasedResolutionSLA from "./charts/PriorityBasedResolutionSLA";
import PieChart from "../email/charts/PieCharts";
import Disposition from "./charts/Disposition";
import ChannelWise from "./charts/ChannelWiseTickets";
import TicketVolume from "../ticket/charts/TicketVolume";
import { useTicketWorkflowIdQuery } from "../../../Services/dashboard/dashboard";
import { useEffect, useMemo, useState } from "react";
import CustomDate from "../../../components/common/CustomDate";
import TotalTickets from "./charts/TotalTickets";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { useGetAllTeamsQuery } from "../../../Services/reports/tickets/ReportTickets";
import PendingVsResolvedTickets from "./charts/PendingVsResolvedTickets";
import { useTicketDashboardQuery } from "../../../Services/dashboard/TicketDashboard";
import ActionableVsNonActionable from "./ActionableVsNonActionable";
import "../DashboardPageLayout.scss";

const TAB_TO_API: Record<string, string> = {
  overview: "overview",
  resolution: "performance",
  analytics: "analytics",
  volume: "volume",
};

const TicketDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [theTeams, setTheTeams] = useState<any[]>([]);
  const [selectedWorkFlow, setSelectedWorkFlow] = useState("");

  const [params, setParams] = useState<any>({
    team: localStorage.getItem("teamId") || "",
    start_date: "",
    end_date: "",
  });

  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const formatToDate = (date: Date): string => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data: teamsData } = useGetAllTeamsQuery({});
  useEffect(() => {
    if (teamsData) setTheTeams(teamsData?.teams || []);
  }, [teamsData]);

  const { data: workflow } = useTicketWorkflowIdQuery(params.team, {
    skip: !params.team,
  });
  useEffect(() => {
    if (workflow?.results?.length > 0) {
      setSelectedWorkFlow(workflow.results[0].id);
    }
  }, [workflow]);

  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setParams((prev: any) => ({ ...prev, team: e.target.value }));
  };

  useEffect(() => {
    if (dateRange?.[0]) {
      setParams((prev: any) => ({
        ...prev,
        start_date: formatToDate(dateRange[0].startDate),
        end_date: formatToDate(dateRange[0].endDate),
      }));
    }
  }, [dateRange]);

  const isPerformanceTab = activeTab === "resolution";
  const canFetch =
    !!params.team &&
    !!params.start_date &&
    !!params.end_date &&
    (isPerformanceTab || !!selectedWorkFlow);

  const workflowStageItems = useMemo(
    () => getWorkflowStageItems(workflow?.results, selectedWorkFlow),
    [workflow?.results, selectedWorkFlow],
  );

  const { data: tabData, isLoading } = useTicketDashboardQuery(
    {
      dashboardType: TAB_TO_API[activeTab],
      selectedTeam: params.team,
      selectedWorkFlow,
      graphStartDate: params.start_date,
      graphEndDate: params.end_date,
    },
    { skip: !canFetch },
  );

  // ── Shared click-to-expand props passed to every chart ───────────────────
  const clickToExpandProps = {
    startDate: params.start_date,
    endDate: params.end_date,
    teamId: params.team || null,
    workflowId: selectedWorkFlow || null,
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: "📊" },
    { id: "resolution", label: "SL Performance", icon: "⏱️" },
    { id: "analytics", label: "Analytics", icon: "🔍" },
    { id: "volume", label: "Volume", icon: "📈" },
  ];

  const renderTabContent = () => {
    // Only replace the tab with a spinner on the first load. Refetches (e.g. after
    // changing the date range in a chart fullscreen modal) keep children mounted so
    // modal state and prior data stay visible until new data arrives.
    if (isLoading && tabData == null) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <LoadingSpinner />
        </div>
      );
    }

    switch (activeTab) {
      case "overview":
        return (
          <>
            <div className="row g-5 g-xl-10 mb-xl-2">
              <div className="col-12 mb-5 mb-xl-0">
                {/* TotalTickets now gets click-to-expand props */}
                <TotalTickets
                  data={tabData}
                  {...clickToExpandProps}
                />
              </div>
            </div>
            <div className="row gx-5 gx-xl-10">
              <div className="col-xl-6 mb-5 mb-xl-10">
                <PieChart
                  overview={[
                    tabData?.tickets_by_status_type?.active || "",
                    tabData?.tickets_by_status_type?.closed || "",
                    tabData?.tickets_by_status_type?.wip || "",
                    tabData?.tickets_by_status_type?.reopen || "",
                  ]}
                  startDate={params.start_date}
                  endDate={params.end_date}
                  teamId={params.team || null}
                  workflowId={selectedWorkFlow || null}
                />
              </div>
              <div className="col-xl-6 mb-5 mb-xl-10">
                {/* Disposition now gets click-to-expand props */}
                <Disposition
                  getDispositionData={tabData}
                  {...clickToExpandProps}
                />
              </div>
            </div>
          </>
        );

      case "resolution":
        return (
          <>
            <div className="row g-5 g-xl-10 mb-xl-2">
              <div className="col-12 mb-5 mb-xl-0">
                <PerformanceSummaryCards data={tabData} />
              </div>
            </div>
            {/* <div className="row g-5 g-xl-10 mb-xl-2">
              <div className="col-12 col-xl-6 mb-5 mb-xl-0">
                <AverageResolutionTime data={tabData} />
              </div>
              <div className="col-12 col-xl-6 mb-5 mb-xl-0">
                <ResolutionBeyondSLA data={tabData} />
              </div>
            </div> */}
            <div className="row g-5 g-xl-10 mb-xl-2">
              <div className="col-12 mb-5 mb-xl-0">
                <TagWiseResolutionSLA data={tabData} {...clickToExpandProps} />
              </div>
            </div>

               <div className="row g-5 g-xl-10 mb-xl-2">
              <div className="col-12 mb-5 mb-xl-0">
                <PriorityBasedResolutionSLA
                  data={tabData}
                  {...clickToExpandProps}
                />
              </div>
            </div>
            <div className="row g-5 g-xl-10 mb-xl-2">
              <div className="col-12 mb-5 mb-xl-0">
                <WorkflowStageResolutionSLA
                  data={tabData}
                  workflowStages={workflowStageItems}
                  {...clickToExpandProps}
                />
              </div>
            </div>
         
          </>
        );

      case "analytics":
        return (
          <div className="row g-5 g-xl-10 mb-xl-2">
            <div className="col-xl-6 mb-5 mb-xl-10 d-flex">
              <ChannelWise channelWiseData={tabData} {...clickToExpandProps} />
            </div>
            <div className="col-xl-6 mb-5 mb-xl-10 d-flex">
              <PendingVsResolvedTickets data={tabData} {...clickToExpandProps} />
            </div>
            <div className="col-xl-6 mb-5 mb-xl-10">
              <ActionableVsNonActionable data={tabData} {...clickToExpandProps} />
            </div>
          </div>
        );

      case "volume":
        return (
          <div className="row g-5 g-xl-10 mb-xl-2">
            <div className="col-12 mb-5 mb-xl-10">
              <TicketVolume data={tabData} {...clickToExpandProps} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dashboard-page">
      <div
        className="dashboard-page__header"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e2e8f0",
          padding: "20px 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Ticket dashboard</h2>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
              Monitor your ticket performance and analytics
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative" }}>
              <CustomDate dateRange={dateRange} setDateRange={setDateRange} />
            </div>

            <div style={{ position: "relative", minWidth: "200px" }}>
              <label
                style={{
                  position: "absolute",
                  top: "-8px",
                  left: "12px",
                  backgroundColor: "#ffffff",
                  padding: "0 4px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#64748b",
                  zIndex: 1,
                }}
              >
                Team
              </label>
              <select
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#1e293b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  outline: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                value={params.team}
                onChange={handleTeamChange}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6366f1";
                  e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="">Select team</option>
                {theTeams.map((team: any) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ position: "relative", minWidth: "200px" }}>
              <label
                style={{
                  position: "absolute",
                  top: "-8px",
                  left: "12px",
                  backgroundColor: "#ffffff",
                  padding: "0 4px",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#64748b",
                  zIndex: 1,
                }}
              >
                Workflow
              </label>
              <select
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#1e293b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  outline: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                value={selectedWorkFlow}
                onChange={(e) => setSelectedWorkFlow(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6366f1";
                  e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="">Select workflow</option>
                {workflow?.results?.map((wf: any) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div
        className="tab-container dashboard-page__tabs"
        style={{
          display: "flex",
          gap: "24px",
          padding: "0 16px",
          width: "100%",
          overflowX: "auto",
          scrollbarWidth: "none",
          borderBottom: "1px solid #e0e0e0",
          position: "relative",
          height: "48px",
          alignItems: "center",
          backgroundColor: "#ffffff",
          marginBottom: "12px",
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              style={{
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
                borderBottom: isActive
                  ? "2px solid #6366f1"
                  : "2px solid transparent",
                marginBottom: "-1px",
                transition: "border-color 0.2s ease",
              }}
            >
              <button
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "12px 16px",
                  margin: 0,
                  border: "none",
                  backgroundColor: "transparent",
                  color: isActive ? "#6366f1" : "#1e293b",
                  cursor: "pointer",
                  fontWeight: isActive ? "600" : "400",
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  outline: "none",
                  transition: "color 0.2s ease",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseOver={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = "#6366f1";
                    (
                      e.currentTarget.closest("div") as HTMLElement
                    ).style.borderBottomColor = "#6366f1";
                  }
                }}
                onMouseOut={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = "#1e293b";
                    (
                      e.currentTarget.closest("div") as HTMLElement
                    ).style.borderBottomColor = "transparent";
                  }
                }}
              >
                <span style={{ fontSize: "1.2rem" }}>{tab.icon}</span>
                <span className="tab-button-text">{tab.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      <div className="dashboard-page__content">
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div className="card-body pt-0">
            {(activeTab === "overview" || activeTab === "resolution") && (
              <div
                className="card card-flush border-0 mb-3"
                style={{
                  padding: "12px 16px",
                  color: "#64748b",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: 1.4,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                }}
              >
                {activeTab === "overview"
                  ? "Only actionable tickets are displayed in this panel"
                  : "Only actionable tickets with SL configured are displayed in this panel"}
              </div>
            )}
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDashboard;
